import { ROLES } from "@user-management-context/shared/domain/types/enums/roles";

export interface AppRoute {
  path: string;
  element: JSX.Element;
}
export const REFUND_REQUEST_ROUTE_PATH = "refund-requests";
export const CREATE_PATH = "create";

// Helper function to determine if a URL is a list route
export const isListRoute = (url: string) => {
  // List routes typically don't have parameters and end with a plural noun
  return !url.includes(":") && url.length > 1;
};

// Helper function to determine if a URL is a detail route
export const isDetailRoute = (path: string, listPath: string) => {
  // Detail routes typically start with the list route path and have an ID segment
  if (!path.startsWith(listPath)) return false;

  // Get the part after the list path
  const remainingPath = path.slice(listPath.length);

  // If it starts with a slash and has content after it (likely an ID)
  // and doesn't match any known sub-routes
  if (remainingPath.startsWith("/") && remainingPath.length > 1) {
    // Check if this is a known sub-route
    const knownSubRoutes = Object.values(ADMIN_URLS)
      .flatMap(routes => (typeof routes === "object" ? Object.values(routes) : []))
      .filter(route => typeof route === "string" && route.startsWith(listPath + "/"));

    // If it's not a known sub-route, it's likely a detail route with an ID
    return !knownSubRoutes.includes(path);
  }

  return false;
};

export const ROLE_BASED_URLS = {
  [ROLES.STUDENT]: {
    dashboard: "/dashboard",
    fundingRequest: {
      new: "/funding-request/new",
      success: "/funding-request/success",
      list: "/funding-requests",
    },
    refundRequest: {
      base: `/${REFUND_REQUEST_ROUTE_PATH}`,
      success: `/${REFUND_REQUEST_ROUTE_PATH}/success`,
    },
    lesson: { list: "/lessons" },
    attendanceSheet: {
      list: "/attendance-sheets",
    },
    trainingItem: {
      list: "/training-items",
      detail: "/training-items/:id",
    },
    externalProviderBatchRequest: {
      new: "/external-provider-batch-requests/new",
    },
    issue: { base: "/issues" },
    teachers: "/teachers",
    profile: "/profile",
    resources: "/resources",
  },
  [ROLES.TEACHER]: {
    dashboard: "/dashboard",
    evaluations: {
      listGroupedByStudent: "/evaluations-grouped-by-student",
      listGroupedByTrainingItem: "/evaluations-grouped-by-training-item",
      details: "/evaluations-grouped-by-student/:studentId",
    },
    lesson: { list: "/lessons" },
    payout: { list: "/payouts" },
    issue: { base: "/issues" },
    profile: "/profile",
    students: { list: "/students" },
    studio: {
      list: "/studios",
      create: "/studios/create",
      detail: "/studios/:id",
    },
    batch: {
      list: "/batches",
      create: "/batches/create",
      detail: "/batches/:id",
      edit: "/batches/:id/edit",
      completeLegacy: "/batches/:id/complete",
    },
    externalTeachers: {
      list: "/external-teachers",
      create: "/external-teachers/create",
      detail: "/external-teachers/:id",
    },
    resources: "/resources",
  },
  [ROLES.PROVIDER]: {
    dashboard: "/dashboard",
    evaluations: {
      listGroupedByStudent: "/evaluations-grouped-by-student",
      listGroupedByTrainingItem: "/evaluations-grouped-by-training-item",
      details: "/evaluations-grouped-by-student/:studentId",
    },
    studio: {
      list: "/studios",
      create: "/studios/create",
      detail: "/studios/:id",
    },
    batch: {
      list: "/batches",
      create: "/batches/create",
      detail: "/batches/:id",
      edit: "/batches/:id/edit",
      completeLegacy: "/batches/:id/complete",
    },
    externalTeachers: {
      list: "/external-teachers",
      create: "/external-teachers/create",
      detail: "/external-teachers/:id",
    },
    issue: { base: "/issues" },
    profile: "/profile",
  },
};

export const ADMIN_URLS = {
  lesson: {
    base: "/lessons",
  },
  balanceAdjustments: {
    base: "/balance-adjustments",
  },
  collaborationRequest: { list: "/collaboration-requests" },
  batchEnrollmentCandidate: { list: "/batch-enrollment-candidates" },
  invitation: {
    list: "/invitations",
  },
  user: {
    list: "/users",
    detail: "/users/:userId",
  },
  provider: {
    list: "/providers",
    detail: "/providers/:id",
    batches: "/provider-batches",
  },
  studio: {
    list: "/studios",
    create: "/studios/create",
    detail: "/studios/:id",
  },
  internalTraining: {
    list: "/internal-trainings",
    create: "/internal-trainings/create",
    detail: "/internal-trainings/:id",
  },
  documentaryResource: {
    list: "/documentary-resources",
  },
  student: {
    list: "/students",
    detail: "/students/:id",
  },
  teacher: {
    list: "/teachers",
    detail: "/teachers/:id",
    batches: "/teacher-batches",
  },
  batch: {
    detail: "/batches/:batchId",
  },
  payment: {
    list: "/payments",
  },
  trainingInvoice: {
    list: "/training-invoices",
  },
  externalProviderBatchRequest: {
    list: "/external-provider-batch-requests",
    edit: "/external-provider-batch-requests/:requestId/edit",
  },
  fundingRequest: {
    list: "/funding-requests",
    detail: "/funding-requests/:id",
    approved: "/funding-requests/approved",
    toSubmit: "/funding-requests/to-submit",
  },
  trainingFollowUp: {
    list: `/training-follow-ups`,
    details: `/training-follow-ups/:id`,
    attendanceSheets: `/training-follow-ups/:id/attendance-sheets`,
    trainingItems: `/training-follow-ups/:id/training-items`,
    fundingRequest: `/training-follow-ups/:id/funding-request`,
  },
  issue: { base: "/issues" },
  profile: "/profile",
  resources: "/resources",
};

// Role-based routes are now lazy-loaded from separate files

// Lazy imports for role-based routes
let adminRoutes: AppRoute[] | null = null;
let studentRoutes: AppRoute[] | null = null;
let teacherRoutes: AppRoute[] | null = null;
let providerRoutes: AppRoute[] | null = null;

const loadAdminRoutes = async (): Promise<AppRoute[]> => {
  if (!adminRoutes) {
    const module = await import("./routes/admin.routes");
    adminRoutes = module.adminRoutes;
  }
  return adminRoutes;
};

const loadStudentRoutes = async (): Promise<AppRoute[]> => {
  if (!studentRoutes) {
    const module = await import("./routes/student.routes");
    studentRoutes = module.studentRoutes;
  }
  return studentRoutes;
};

const loadTeacherRoutes = async (): Promise<AppRoute[]> => {
  if (!teacherRoutes) {
    const module = await import("./routes/teacher.routes");
    teacherRoutes = module.teacherRoutes;
  }
  return teacherRoutes;
};

const loadProviderRoutes = async (): Promise<AppRoute[]> => {
  if (!providerRoutes) {
    const module = await import("./routes/provider.routes");
    providerRoutes = module.providerRoutes;
  }
  return providerRoutes;
};

const generateRoleBasedRoutes = async (role: ROLES | null, isAdmin = false) => {
  let routes: AppRoute[] = [];
  let defaultRoute = "dashboard";

  if (isAdmin) {
    routes = await loadAdminRoutes();
    defaultRoute = "payments";
  } else if (role === ROLES.STUDENT) {
    routes = await loadStudentRoutes();
  } else if (role === ROLES.TEACHER) {
    routes = await loadTeacherRoutes();
  } else if (role === ROLES.PROVIDER) {
    routes = await loadProviderRoutes();
  }

  return { routes, defaultRoute };
};

export { generateRoleBasedRoutes };
